import { ReactElement, useState } from "react";
import { t } from "@lingui/core/macro";

import {
  Project,
  Attempt,
  useProjects,
  useAllAttempts,
  Revision,
  useAttemptDatasetManifest,
  sortAttempts,
  AttemptDatasetManifest,
} from "_/data/projects";
import { Machine } from "_/data/machines";
import { Material, useMaterials } from "_/data/materials";

import { Uuid } from "_/types";
import { indexById } from "_/utils";

import { ColumnDef, CustomCellLink, Table } from "_/components/table";
import { DateTime } from "_/components/date-time";
import { Button, ButtonGroup } from "_/components/button";
import { Link, NeverUnderlineSpace } from "_/components/router";
import { Modal } from "_/components/modal";
import { AttemptDataDownloadList } from "_/components/attempt-data-download-list";
import { StatusBadge } from "_/components/badge";
import { MaterialsList } from "_/components/materials";
import { NumberBadge } from "_/components/number-badge";

import { projectUrls } from "_/routes";

import * as S from "./styled";

type ModalProps = {
  projectId: Uuid;
  attemptId: Uuid;
  manifest: AttemptDatasetManifest[];
};

const AttemptActionButtons = ({
  revision,
  attempt,
  setModalProps,
}: {
  revision: Revision;
  attempt: Attempt;
  setModalProps: (modalProps: ModalProps) => void;
}): ReactElement => {
  const manifestQuery = useAttemptDatasetManifest(
    { projectId: revision.projectId, attempt },
    { enabled: false }
  );

  return (
    <ButtonGroup>
      {attempt.endedAt && (
        <Button
          size="small"
          kind="secondary"
          onMouseOver={() => {
            // Only attempt to load the data if it hasn't been loaded yet.
            if (manifestQuery.isStale || !manifestQuery.data) {
              void manifestQuery.refetch();
            }
          }}
          onClick={() =>
            setModalProps({
              attemptId: attempt.id,
              manifest: manifestQuery.data?.manifest ?? [],
              projectId: revision.projectId,
            })
          }
        >
          {t`common.download`}
        </Button>
      )}
      <Link to={projectUrls.analyze(revision.projectId, attempt.id)}>
        <Button size="small">{t`common.analyze`}</Button>
      </Link>
    </ButtonGroup>
  );
};

export interface JobsListingProps {
  /** Maximum number of jobs to render. */
  limit: number;

  /** Filter results to this machine */
  machine: Machine;
}

const AttemptNumber = ({
  revision,
  attempt,
}: {
  revision: Revision;
  attempt: Attempt;
}) => {
  return (
    <S.RevisionOrJobNumber
      title={t`common.job-description ${revision.number} ${attempt.number}`}
    >
      #{revision.number}-{attempt.number}
    </S.RevisionOrJobNumber>
  );
};

export function RevisionCell({
  project,
  revision,
}: {
  project: Project;
  revision: Revision;
}): ReactElement {
  return (
    <CustomCellLink to={projectUrls.prepare(project.id, revision.id)}>
      <span>{project.name}</span>
      <NeverUnderlineSpace />
      <NumberBadge number={revision.number} />
    </CustomCellLink>
  );
}

export function AttemptCell({
  project,
  revision,
  attempt,
}: {
  project: Project;
  revision: Revision;
  attempt: Attempt;
}): ReactElement {
  return (
    <CustomCellLink to={projectUrls.analyze(project.id, attempt.id)}>
      <span>{project.name}</span>
      <NeverUnderlineSpace />
      <AttemptNumber revision={revision} attempt={attempt} />
    </CustomCellLink>
  );
}

type RowData = {
  attempt: Attempt;
  project: Project;
  revision: Revision;
  material0?: Material | undefined;
  material1?: Material | undefined;
  setModalProps: (modalProps: ModalProps) => void;
};

const columns: ColumnDef<RowData>[] = [
  {
    id: "projectAndRevision",
    header: () => t`common.project-and-revision`,
    meta: { customTd: true },
    cell: ({ row: { original: rowData } }) => (
      <RevisionCell project={rowData.project} revision={rowData.revision} />
    ),
  },
  {
    id: "materials",
    header: () => t`common.materials`,
    cell: ({ row: { original: rowData } }) => {
      const materialsInner = [rowData.material0, rowData.material1].filter(
        Boolean
      );
      return <MaterialsList materials={materialsInner} />;
    },
  },
  {
    id: "startDate",
    header: () => t`common.start-date`,
    cell: ({ row: { original: rowData } }) => (
      <DateTime value={rowData.attempt.startedAt} />
    ),
  },
  {
    id: "endDate",
    header: () => t`common.end-date`,
    cell: ({ row: { original: rowData } }) => (
      <DateTime value={rowData.attempt.endedAt} />
    ),
  },
  {
    id: "status",
    header: () => t`common.status`,
    cell: ({ row: { original: rowData } }) => (
      <StatusBadge status={rowData.attempt.status} />
    ),
  },
  {
    id: "actions",
    header: () => t`common.actions`,
    cell: ({ row: { original: rowData } }) => (
      <AttemptActionButtons
        revision={rowData.revision}
        attempt={rowData.attempt}
        setModalProps={rowData.setModalProps}
      />
    ),
  },
];

export const JobsListing = ({ limit, machine }: JobsListingProps) => {
  const [modalProps, setModalProps] = useState<ModalProps | null>(null);

  const allAttemptsResult = useAllAttempts();
  const { data: projects = [] } = useProjects();
  const revisions = projects.flatMap((p) => p.revisions) ?? [];
  const { data: materials = [] } = useMaterials();
  const machineId = machine?.id;

  if (limit < 1) {
    throw new Error("`limit` must be >= 1");
  }

  const attempts = (allAttemptsResult.data ?? []).filter(
    (a) => a.machineId === machineId
  );

  const projectByKey = indexById(projects);
  const materialByKey = indexById(materials);
  const revisionsByKey = indexById(revisions);

  const topJobs = attempts.toSorted(sortAttempts).slice(0, limit);

  // Denormalize the attempts.
  const tableData: RowData[] = topJobs
    .map((attempt) => {
      const revision = revisionsByKey[attempt.revisionId];
      if (!revision) return undefined;
      const project = projectByKey[revision.projectId];
      if (!project) return undefined;
      const material0 = revision.materials[0]
        ? materialByKey[revision.materials[0]]
        : undefined;
      const material1 = revision.materials[1]
        ? materialByKey[revision.materials[1]]
        : undefined;

      return {
        attempt,
        project,
        revision,
        material0,
        material1,
        setModalProps,
      };
    })
    .filter(Boolean);

  return (
    <>
      <Table
        columns={columns}
        data={tableData}
        loading={allAttemptsResult.isLoading}
      />
      <Modal
        title={t`components.attempts-listing.downloads`}
        open={modalProps !== null}
        onClose={() => {
          setModalProps(null);
        }}
      >
        {modalProps && (
          <AttemptDataDownloadList
            projectId={modalProps.projectId}
            attemptId={modalProps.attemptId}
            manifest={modalProps.manifest}
          />
        )}
      </Modal>
    </>
  );
};
